import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useSearch } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { useAuth } from '@/common/hooks/useAuth'
import { useImpersonation } from '@/common/hooks/useImpersonation'
import {
  useCurrentTimesheet,
  usePayPeriods,
  useUpdateEntries,
  useSubmitTimesheet,
  type AdjacentContextEntry,
  type TimeEntry,
  type TimeEntryUpdatePayload,
} from '@/common/hooks/useTimesheets'
import { getAllowedPeriods, getDefaultPeriod } from '@/common/utils/payPeriodNavigation'
import {
  isOffDay,
  copyWorkedTimeToWorkingDays,
  findOverwriteTargets,
  hasWorkedTime,
  type CopyableDay,
} from '@/common/lib/workSchedule'
import { Button } from '@/common/components/button'
import { Tooltip } from '@/common/components/tooltip'
import { WandSparkles, MessageSquarePlus, MessageSquareText, Plus, Minus } from 'lucide-react'
import * as styles from './TimesheetPage.css'
import { colors } from '@/core/styles/tokens'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(dateStr: string) {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

function formatDateShort(dateStr: string) {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' })
}

function formatPeriod(start: string, end: string) {
  const s = new Date(start + 'T00:00:00')
  const e = new Date(end + 'T00:00:00')
  const fmt: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' }
  return `${s.toLocaleDateString('en-US', fmt)} – ${e.toLocaleDateString('en-US', fmt)}`
}


function addDays(dateStr: string, days: number) {
  const d = new Date(dateStr + 'T00:00:00')
  d.setDate(d.getDate() + days)
  return d.toISOString().slice(0, 10)
}

function statusStyle(status: string) {
  switch (status) {
    case 'submitted':
      return styles.statusSubmitted
    case 'approved':
      return styles.statusApproved
    case 'rejected':
      return styles.statusRejected
    default:
      return styles.statusDraft
  }
}

/** Compute hours worked from clock-in/out pairs (client-side for display). */
function computeHoursWorked(
  clockIn1: string,
  clockOut1: string,
  clockIn2: string,
  clockOut2: string,
): number {
  let total = 0
  for (const [cin, cout] of [
    [clockIn1, clockOut1],
    [clockIn2, clockOut2],
  ] as const) {
    if (cin && cout) {
      const [h1, m1] = cin.split(':').map(Number)
      const [h2, m2] = cout.split(':').map(Number)
      const diff = h2 * 60 + m2 - (h1 * 60 + m1)
      if (diff > 0) total += diff
    }
  }
  return Math.round((total / 60) * 100) / 100
}

/** Convert "HH:MM" to "HH:MM:00" for API, or null if empty. */
function timeToApi(val: string): string | null {
  if (!val) return null
  return val.length === 5 ? val + ':00' : val
}

/** Coerce an hours field to a finite number. Blank or unparseable input becomes 0. */
function toHours(value: unknown): number {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

/** Return the Sunday that starts the work week containing the given date. */
function getWeekSunday(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  const day = d.getDay() // 0=Sun, 6=Sat
  d.setDate(d.getDate() - day)
  return d.toISOString().slice(0, 10)
}

/** Format a period range as "Mon D – Mon D" (short). */
function formatPeriodShort(start: string, end: string) {
  const s = new Date(start + 'T00:00:00')
  const e = new Date(end + 'T00:00:00')
  const fmt: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }
  return `${s.toLocaleDateString('en-US', fmt)} – ${e.toLocaleDateString('en-US', fmt)}`
}

/** A row in the unified grid: either a current-period entry or an adjacent-context entry. */
interface GridRow {
  date: string
  type: 'current' | 'adjacent'
  entryIndex?: number // index into form entries (only for 'current')
  adjacentEntry?: AdjacentContextEntry // only for 'adjacent'
}

/** Weekly subtotal data. */
interface WeekSubtotal {
  weekSunday: string
  totalHours: number
  standardHours: number
  flsaOvertimeHours: number
  straightOvertimeHours: number
  /** True when the week extends beyond the pay period and has no adjacent context from the next period. */
  isIncomplete: boolean
}

// ---------------------------------------------------------------------------
// Form types
// ---------------------------------------------------------------------------

interface EntryValues {
  date: string
  clock_in_1: string
  clock_out_1: string
  clock_in_2: string
  clock_out_2: string
  vacation_hours: number
  comment: string
}

interface FormValues {
  entries: EntryValues[]
}

type SaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'error'

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TimesheetPage() {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth()
  const { isImpersonating, impersonatedRoles } = useImpersonation()
  const { period_start } = useSearch({ from: '/timesheets' })
  const [periodStart, setPeriodStart] = useState<string | undefined>(period_start)
  const [defaultApplied, setDefaultApplied] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())
  const [commentPopover, setCommentPopover] = useState<{ index: number; readOnly: boolean } | null>(null)
  // Pending "copy to working days" awaiting confirmation (set only when one or
  // more target working days would be overwritten).
  const [copyConfirm, setCopyConfirm] = useState<{ sourceIndex: number; overwriteCount: number } | null>(null)

  // Queries
  const { data: timesheet, isLoading, error } = useCurrentTimesheet(periodStart)
  const { data: payPeriods } = usePayPeriods()

  // Role-aware period restriction: use impersonated roles when impersonating
  const roles = useMemo(
    () => (isImpersonating ? impersonatedRoles : (user?.roles ?? [])),
    [isImpersonating, impersonatedRoles, user?.roles],
  )
  const { previous, current, isRestricted } = useMemo(
    () => (payPeriods ? getAllowedPeriods(payPeriods, roles) : { previous: null, current: null, isRestricted: false }),
    [payPeriods, roles],
  )

  // Apply default period on initial load (no period_start param)
  useEffect(() => {
    if (defaultApplied || period_start || !isRestricted) return
    if (!previous && !current) return
    const defaultPeriod = getDefaultPeriod(previous, current)
    if (defaultPeriod) {
      setPeriodStart(defaultPeriod.start_date)
      setDefaultApplied(true)
    }
  }, [defaultApplied, period_start, isRestricted, previous, current])

  // Mutations
  const updateEntries = useUpdateEntries(timesheet?.id)
  const submitTimesheet = useSubmitTimesheet(timesheet?.id)

  // Build dates from start_date to end_date (handles variable-length periods)
  const dates = useMemo(() => {
    if (!timesheet) return []
    const result: string[] = []
    let cur = timesheet.pay_period.start_date
    const end = timesheet.pay_period.end_date
    while (cur <= end) {
      result.push(cur)
      cur = addDays(cur, 1)
    }
    return result
  }, [timesheet])

  // Entry lookup by date
  const entryByDate = useMemo(() => {
    if (!timesheet) return new Map<string, TimeEntry>()
    const map = new Map<string, TimeEntry>()
    timesheet.entries.forEach((e) => map.set(e.date, e))
    return map
  }, [timesheet])

  // Build unified grid rows: adjacent (before) + current + adjacent (after)
  const gridRows = useMemo<GridRow[]>(() => {
    if (!timesheet) return []

    const adjacentContext = timesheet.adjacent_context ?? []
    const prevAdjacent = adjacentContext
      .filter((a) => a.date < timesheet.pay_period.start_date)
      .sort((a, b) => a.date.localeCompare(b.date))
    const nextAdjacent = adjacentContext
      .filter((a) => a.date > timesheet.pay_period.end_date)
      .sort((a, b) => a.date.localeCompare(b.date))

    const rows: GridRow[] = []

    // Adjacent rows before current period
    for (const adj of prevAdjacent) {
      rows.push({ date: adj.date, type: 'adjacent', adjacentEntry: adj })
    }

    // Current period rows
    dates.forEach((d, i) => {
      rows.push({ date: d, type: 'current', entryIndex: i })
    })

    // Adjacent rows after current period
    for (const adj of nextAdjacent) {
      rows.push({ date: adj.date, type: 'adjacent', adjacentEntry: adj })
    }

    return rows
  }, [timesheet, dates])

  // Auto-expand rows with existing second clock pair on data load
  useMemo(() => {
    if (!timesheet) return
    const expanded = new Set<number>()
    dates.forEach((d, i) => {
      const entry = entryByDate.get(d)
      if (entry?.clock_in_2 || entry?.clock_out_2) {
        expanded.add(i)
      }
    })
    if (expanded.size > 0) {
      setExpandedRows((prev) => new Set([...prev, ...expanded]))
    }
  }, [timesheet, dates, entryByDate])

  // Form — binds clock times and vacation_hours
  const form = useForm<FormValues>({
    values: timesheet
      ? {
          entries: dates.map((d) => {
            const entry = entryByDate.get(d)
            return {
              date: d,
              clock_in_1: entry?.clock_in_1?.slice(0, 5) ?? '',
              clock_out_1: entry?.clock_out_1?.slice(0, 5) ?? '',
              clock_in_2: entry?.clock_in_2?.slice(0, 5) ?? '',
              clock_out_2: entry?.clock_out_2?.slice(0, 5) ?? '',
              vacation_hours: entry?.vacation_hours ?? 0,
              comment: entry?.comment ?? '',
            }
          }),
        }
      : undefined,
  })

  const isDraftOrRejected = timesheet?.status === 'draft' || timesheet?.status === 'rejected'
  const isEditable = !isImpersonating && isDraftOrRejected

  // Admin impersonation: can submit even when inputs are disabled
  const canShowSubmit = isDraftOrRejected

  // Pay period info
  const currentPeriodInfo = useMemo(() => {
    if (!payPeriods || !timesheet) return null
    return payPeriods.find(
      (pp) =>
        pp.start_date === timesheet.pay_period.start_date &&
        pp.end_date === timesheet.pay_period.end_date,
    )
  }, [payPeriods, timesheet])
  const periodState = currentPeriodInfo?.state
  const submissionDeadline = currentPeriodInfo?.submission_deadline
  // Submission is allowed once the period has ended — both `active` and
  // `closed` (past-deadline) states. Only `drafting` blocks it.
  // Admin impersonation bypasses the submission window on the backend.
  const canSubmit =
    isDraftOrRejected &&
    (isImpersonating || periodState === 'active' || periodState === 'closed')

  // Compute totals from watched form values + server data
  const watchedEntries = form.watch('entries')

  const totals = useMemo(() => {
    let hoursWorked = 0
    let holidayHours = 0
    let vacationHours = 0
    let total = 0

    dates.forEach((d, i) => {
      const entry = entryByDate.get(d)
      const we = watchedEntries?.[i]
      const hw = we
        ? computeHoursWorked(we.clock_in_1, we.clock_out_1, we.clock_in_2, we.clock_out_2)
        : 0
      const hh = entry?.holiday_hours ?? 0
      const vh = Number(we?.vacation_hours) || 0

      hoursWorked += hw
      holidayHours += hh
      vacationHours += vh
      total += hw + hh + vh
    })

    return { hoursWorked, holidayHours, vacationHours, total }
  }, [dates, watchedEntries, entryByDate])

  // Weekly subtotals — current-period rows use live form values so the
  // displayed week totals update as the employee enters hours (overtime
  // classification stays at last-saved values until the next save).
  const weekSubtotals = useMemo<WeekSubtotal[]>(() => {
    if (!timesheet) return []

    const weekMap = new Map<string, WeekSubtotal>()
    for (const row of gridRows) {
      const sun = getWeekSunday(row.date)
      if (!weekMap.has(sun)) {
        weekMap.set(sun, {
          weekSunday: sun,
          totalHours: 0,
          standardHours: 0,
          flsaOvertimeHours: 0,
          straightOvertimeHours: 0,
          isIncomplete: false,
        })
      }
      const ws = weekMap.get(sun)!
      if (row.type === 'adjacent' && row.adjacentEntry) {
        const a = row.adjacentEntry
        ws.totalHours += a.hours_worked + a.holiday_hours + a.vacation_hours
        ws.standardHours += a.standard_hours
        ws.flsaOvertimeHours += a.flsa_overtime_hours
        ws.straightOvertimeHours += a.straight_overtime_hours
      } else if (row.type === 'current') {
        const entry = entryByDate.get(row.date)
        const we = row.entryIndex !== undefined ? watchedEntries?.[row.entryIndex] : undefined
        const hw = we
          ? computeHoursWorked(we.clock_in_1, we.clock_out_1, we.clock_in_2, we.clock_out_2)
          : 0
        const hh = entry?.holiday_hours ?? 0
        const vh = Number(we?.vacation_hours) || 0
        ws.totalHours += hw + hh + vh
        // Overtime split is computed server-side; reflect last-saved values.
        if (entry) {
          ws.standardHours += entry.standard_hours
          ws.flsaOvertimeHours += entry.flsa_overtime_hours
          ws.straightOvertimeHours += entry.straight_overtime_hours
        }
      }
    }

    // Mark weeks that extend past the period end with no adjacent context as incomplete
    const periodEnd = timesheet.pay_period.end_date
    const nextAdjacentSundays = new Set(
      gridRows
        .filter((r) => r.type === 'adjacent' && r.date > periodEnd)
        .map((r) => getWeekSunday(r.date)),
    )
    for (const ws of weekMap.values()) {
      const weekSaturday = addDays(ws.weekSunday, 6)
      if (weekSaturday > periodEnd && !nextAdjacentSundays.has(ws.weekSunday)) {
        ws.isIncomplete = true
      }
    }

    return [...weekMap.values()]
  }, [timesheet, gridRows, entryByDate, watchedEntries])

  // Build API payload from form values
  const buildPayload = useCallback(
    (data: FormValues): TimeEntryUpdatePayload[] => {
      return data.entries.map((e) => {
        const hw = computeHoursWorked(e.clock_in_1, e.clock_out_1, e.clock_in_2, e.clock_out_2)
        return {
          date: e.date,
          hours_worked: hw,
          // Guarded again here: JSON.stringify turns a stray NaN into null, which
          // the API rejects outright rather than falling back to its 0 default.
          vacation_hours: toHours(e.vacation_hours),
          clock_in_1: timeToApi(e.clock_in_1),
          clock_out_1: timeToApi(e.clock_out_1),
          clock_in_2: timeToApi(e.clock_in_2),
          clock_out_2: timeToApi(e.clock_out_2),
          comment: e.comment || null,
        }
      })
    },
    [],
  )

  // ---- Auto-save ----
  const [saveState, setSaveState] = useState<SaveState>('idle')
  const dirtyRef = useRef(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const performSave = useCallback(async () => {
    if (!isEditable) return
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
    dirtyRef.current = false
    setSaveState('saving')
    try {
      await updateEntries.mutateAsync(buildPayload(form.getValues()))
      // If new edits arrived while saving, remain dirty for the next cycle.
      setSaveState(dirtyRef.current ? 'dirty' : 'saved')
    } catch {
      setSaveState('error')
    }
  }, [isEditable, updateEntries, buildPayload, form])

  // Keep a stable ref to the latest performSave so the watch subscription
  // below does not need to re-subscribe (and reset its debounce) every render.
  const performSaveRef = useRef(performSave)
  useEffect(() => {
    performSaveRef.current = performSave
  }, [performSave])

  // Debounced auto-save on editable field changes
  useEffect(() => {
    if (!isEditable) return
    const sub = form.watch((_value, info) => {
      if (info.type !== 'change') return
      dirtyRef.current = true
      setSaveState('dirty')
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        void performSaveRef.current()
      }, 2000)
    })
    return () => {
      sub.unsubscribe()
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [form, isEditable])

  // Best-effort flush if the user leaves with unsaved edits
  useEffect(() => {
    if (!isEditable) return
    const handler = (e: BeforeUnloadEvent) => {
      if (!dirtyRef.current) return
      updateEntries.mutate(buildPayload(form.getValues()))
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isEditable, updateEntries, buildPayload, form])

  // Flush a pending save when focus leaves a field
  const handleFieldBlur = useCallback(() => {
    if (isEditable && dirtyRef.current) void performSave()
  }, [isEditable, performSave])

  // Submit flow
  const handleSubmitClick = () => setShowConfirm(true)
  const handleConfirmSubmit = async () => {
    const data = form.getValues()
    await updateEntries.mutateAsync(buildPayload(data))
    await submitTimesheet.mutateAsync()
    setShowConfirm(false)
  }

  // Navigation — use day-before-start / day-after-end for correct semi-monthly navigation
  const handlePrev = () => {
    if (timesheet) {
      setPeriodStart(addDays(timesheet.pay_period.start_date, -1))
    }
  }
  const handleNext = () => {
    if (timesheet) {
      setPeriodStart(addDays(timesheet.pay_period.end_date, 1))
    }
  }

  const sortedPeriods = useMemo(
    () => payPeriods?.slice().sort((a, b) => a.start_date.localeCompare(b.start_date)),
    [payPeriods],
  )
  const isFirstPeriod =
    sortedPeriods && timesheet
      ? timesheet.pay_period.start_date <= sortedPeriods[0]?.start_date
      : false

  // Toggle expand for a row
  const toggleExpand = (rowIndex: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(rowIndex)) {
        // Clear second pair values when collapsing
        form.setValue(`entries.${rowIndex}.clock_in_2`, '')
        form.setValue(`entries.${rowIndex}.clock_out_2`, '')
        next.delete(rowIndex)
      } else {
        next.add(rowIndex)
      }
      return next
    })
  }

  // ---- Copy worked time to all working days ----
  // Snapshot the current form entries as the minimal shape the pure copy logic
  // needs (date + worked-time clock fields only).
  const copyableDays = useCallback(
    (): CopyableDay[] =>
      form.getValues('entries').map((e) => ({
        date: e.date,
        clock_in_1: e.clock_in_1,
        clock_out_1: e.clock_out_1,
        clock_in_2: e.clock_in_2,
        clock_out_2: e.clock_out_2,
      })),
    [form],
  )

  // Apply the copy: overlay worked-time onto every other working day, expand
  // any rows that gained a second clock pair, mark dirty, and trigger a save.
  const applyCopy = useCallback(
    (sourceIndex: number) => {
      const days = copyableDays()
      const result = copyWorkedTimeToWorkingDays(days, sourceIndex, timesheet?.work_days)
      const expand = new Set<number>()
      result.forEach((day, i) => {
        if (i === sourceIndex) return
        const before = days[i]
        if (
          before.clock_in_1 === day.clock_in_1 &&
          before.clock_out_1 === day.clock_out_1 &&
          before.clock_in_2 === day.clock_in_2 &&
          before.clock_out_2 === day.clock_out_2
        ) {
          return // unchanged (non-working day or already identical)
        }
        form.setValue(`entries.${i}.clock_in_1`, day.clock_in_1, { shouldDirty: true })
        form.setValue(`entries.${i}.clock_out_1`, day.clock_out_1, { shouldDirty: true })
        form.setValue(`entries.${i}.clock_in_2`, day.clock_in_2, { shouldDirty: true })
        form.setValue(`entries.${i}.clock_out_2`, day.clock_out_2, { shouldDirty: true })
        if (day.clock_in_2 || day.clock_out_2) expand.add(i)
      })
      if (expand.size > 0) {
        setExpandedRows((prev) => new Set([...prev, ...expand]))
      }
      void performSave()
    },
    [copyableDays, timesheet?.work_days, form, performSave],
  )

  // Entry point from the row icon: confirm only when targets would be
  // overwritten; otherwise apply immediately (one click).
  const handleCopyClick = useCallback(
    (sourceIndex: number) => {
      const overwrites = findOverwriteTargets(copyableDays(), sourceIndex, timesheet?.work_days)
      if (overwrites.length > 0) {
        setCopyConfirm({ sourceIndex, overwriteCount: overwrites.length })
      } else {
        applyCopy(sourceIndex)
      }
    },
    [copyableDays, timesheet?.work_days, applyCopy],
  )

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (authLoading || isLoading) {
    return <div className={styles.page}>Loading...</div>
  }

  if (!isAuthenticated) {
    return <div className={styles.page}>Please log in to view your timesheet.</div>
  }

  if (error) {
    return <div className={styles.page}>Error: {error.message}</div>
  }

  if (!timesheet) {
    return <div className={styles.page}>No timesheet found.</div>
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <h1 className={styles.title}>Timesheet</h1>
        <div className={styles.actions}>
          <span className={statusStyle(timesheet.status)}>{timesheet.status}</span>
          {isEditable && (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void performSave()}
                disabled={saveState === 'saving'}
              >
                {saveState === 'saving' ? 'Saving...' : 'Save'}
              </Button>
              {saveState !== 'idle' && (
                <span
                  className={`${styles.saveIndicator} ${
                    saveState === 'saved'
                      ? styles.saveIndicatorSaved
                      : saveState === 'error'
                        ? styles.saveIndicatorError
                        : styles.saveIndicatorPending
                  }`}
                  role="status"
                  aria-live="polite"
                >
                  {saveState === 'saving'
                    ? 'Saving…'
                    : saveState === 'saved'
                      ? 'Changes saved'
                      : saveState === 'error'
                        ? 'Save failed — retry'
                        : 'Unsaved changes'}
                </span>
              )}
            </>
          )}
          {canShowSubmit && (
            <Button
              size="sm"
              onClick={handleSubmitClick}
              disabled={submitTimesheet.isPending || !canSubmit}
              title={
                !isImpersonating && periodState === 'drafting'
                  ? 'Submission opens after the pay period ends'
                  : undefined
              }
            >
              Submit
            </Button>
          )}
        </div>
      </div>

      {/* Rejection reason */}
      {timesheet.status === 'rejected' && timesheet.rejection_reason && (
        <div className={styles.rejectionReason}>
          <strong>Rejected:</strong> {timesheet.rejection_reason}
        </div>
      )}

      {/* Period navigation */}
      {isRestricted ? (
        <div className={styles.periodNav}>
          {previous && (
            <button
              className={`${styles.navButton} ${timesheet.pay_period.start_date === previous.start_date ? styles.navButtonActive : ''}`}
              onClick={() => setPeriodStart(previous.start_date)}
            >
              {formatPeriod(previous.start_date, previous.end_date)}
            </button>
          )}
          {current && (
            <button
              className={`${styles.navButton} ${timesheet.pay_period.start_date === current.start_date ? styles.navButtonActive : ''}`}
              onClick={() => setPeriodStart(current.start_date)}
            >
              {formatPeriod(current.start_date, current.end_date)}
            </button>
          )}
        </div>
      ) : (
        <div className={styles.periodNav}>
          <button className={styles.navButton} onClick={handlePrev} disabled={isFirstPeriod}>
            &larr; Previous
          </button>
          <span className={styles.periodLabel}>
            {formatPeriod(timesheet.pay_period.start_date, timesheet.pay_period.end_date)}
          </span>
          <button className={styles.navButton} onClick={handleNext}>
            Next &rarr;
          </button>
        </div>
      )}

      {/* Deadline info */}
      {submissionDeadline && periodState === 'active' && (
        <div className={styles.deadlineInfo}>
          Submission deadline: {formatDate(submissionDeadline)}
        </div>
      )}
      {periodState === 'drafting' && isEditable && (
        <div className={styles.deadlineInfo}>
          Submission opens after the pay period ends
          {submissionDeadline && <> (deadline: {formatDate(submissionDeadline)})</>}
        </div>
      )}
      {periodState === 'closed' && isEditable && (
        <div className={styles.deadlineWarning}>
          The submission deadline has passed. Please submit this timesheet as soon as possible —
          it will still be sent to your manager for approval.
        </div>
      )}

      {/* OT Legend */}
      <div className={styles.otLegend}>
        <span className={styles.otLegendItem}>
          <span className={styles.otDotFlsa} /> OT (1.5x)
        </span>
        <span className={styles.otLegendItem}>
          <span className={styles.otDotStraight} /> Straight OT (1.0x)
        </span>
        <span className={styles.otLegendItem}>
          <span className={styles.otDotPending} /> OT pending
        </span>
      </div>

      {/* Day-as-rows Grid */}
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void performSave()
        }}
        onBlur={handleFieldBlur}
      >
        <div className={styles.gridScroll}>
          <table className={styles.grid}>
            <thead>
              <tr>
                <th className={styles.gridHeaderCell} style={{ textAlign: 'left' }}>
                  Day
                </th>
                <th className={styles.gridHeaderCell}>Time In</th>
                <th className={styles.gridHeaderCell}>Time Out</th>
                <th className={styles.gridHeaderCell} style={{ width: 32 }}></th>
                <th className={styles.gridHeaderCell}>Time In 2</th>
                <th className={styles.gridHeaderCell}>Time Out 2</th>
                <th className={styles.gridHeaderCell}>Hours Worked</th>
                <th className={styles.gridHeaderCell}>Holiday</th>
                <th className={styles.gridHeaderCell}>PTO</th>
                <th className={styles.gridHeaderCell}>Total</th>
                <th className={styles.gridHeaderCell} style={{ width: 36 }}></th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const renderedRows: React.ReactNode[] = []
                let lastWeekSunday: string | null = null

                for (const row of gridRows) {
                  const weekSunday = getWeekSunday(row.date)

                  // Insert week subtotal before starting a new week (except the first)
                  if (lastWeekSunday !== null && weekSunday !== lastWeekSunday) {
                    const subtotal = weekSubtotals.find((ws) => ws.weekSunday === lastWeekSunday)
                    if (subtotal) {
                      renderedRows.push(
                        <tr key={`ws-${lastWeekSunday}`} className={styles.weekSubtotalRow}>
                          <td className={styles.weekSubtotalLabel}>
                            Week Total
                            {subtotal.isIncomplete && (
                              <span style={{ color: colors.gray50, fontWeight: 'normal', marginLeft: 4, fontSize: '0.75rem' }}>
                                (partial week)
                              </span>
                            )}
                          </td>
                          <td className={styles.weekSubtotalValue} colSpan={5}></td>
                          <td className={styles.weekSubtotalValue}></td>
                          <td className={styles.weekSubtotalValue}></td>
                          <td className={styles.weekSubtotalValue}></td>
                          <td className={styles.weekSubtotalValue}></td>
                          <td className={styles.weekSubtotalValue}>
                            {subtotal.totalHours > 0 ? subtotal.totalHours.toFixed(2) : ''}
                            {subtotal.isIncomplete ? (
                              <span className={styles.otIndicator} title="OT pending — week spans into next pay period">
                                <span className={styles.otDotPending} />
                              </span>
                            ) : (
                              <>
                                {subtotal.flsaOvertimeHours > 0 && (
                                  <span className={styles.otIndicator} title={`${subtotal.flsaOvertimeHours.toFixed(2)} hrs OT (1.5x)`}>
                                    <span className={styles.otDotFlsa} />
                                    <span className={styles.otValueFlsa}>{subtotal.flsaOvertimeHours.toFixed(2)}</span>
                                  </span>
                                )}
                                {subtotal.straightOvertimeHours > 0 && (
                                  <span className={styles.otIndicator} title={`${subtotal.straightOvertimeHours.toFixed(2)} hrs Straight OT (1.0x)`}>
                                    <span className={styles.otDotStraight} />
                                    <span className={styles.otValueStraight}>{subtotal.straightOvertimeHours.toFixed(2)}</span>
                                  </span>
                                )}
                              </>
                            )}
                          </td>
                        </tr>,
                      )
                    }
                  }
                  lastWeekSunday = weekSunday

                  if (row.type === 'adjacent' && row.adjacentEntry) {
                    const adj = row.adjacentEntry
                    const adjTotal = adj.hours_worked + adj.holiday_hours + adj.vacation_hours
                    renderedRows.push(
                      <tr key={`adj-${row.date}`} className={`${styles.dayRow} ${styles.adjacentRow}`}>
                        <td className={styles.dayLabel}>
                          {formatDateShort(row.date)}
                          <span className={styles.adjacentLabel}>
                            From {formatPeriodShort(adj.pay_period_start, adj.pay_period_end)}
                          </span>
                        </td>
                        <td className={styles.emptyCell} colSpan={5}></td>
                        <td className={`${styles.computedCell} ${styles.readonlyValue}`}>
                          {adj.hours_worked > 0 ? adj.hours_worked.toFixed(2) : ''}
                        </td>
                        <td className={`${styles.computedCell} ${styles.readonlyValue}`}>
                          {adj.holiday_hours > 0 ? adj.holiday_hours.toFixed(0) : ''}
                        </td>
                        <td className={`${styles.computedCell} ${styles.readonlyValue}`}>
                          {adj.vacation_hours > 0 ? adj.vacation_hours.toFixed(0) : ''}
                        </td>
                        <td className={`${styles.computedCell} ${styles.readonlyValue}`}>
                          {adjTotal > 0 ? adjTotal.toFixed(2) : ''}
                        </td>
                        <td className={styles.commentCell}>
                          {adj.comment && (
                            <button
                              type="button"
                              className={styles.commentIconFilled}
                              title={adj.comment}
                              onClick={() => { /* adjacent comments are view-only via tooltip */ }}
                            >
                              <MessageSquareText size={15} aria-hidden="true" />
                            </button>
                          )}
                        </td>
                      </tr>,
                    )
                  } else if (row.type === 'current' && row.entryIndex !== undefined) {
                    const i = row.entryIndex
                    const d = row.date
                    const entry = entryByDate.get(d)
                    const we = watchedEntries?.[i]
                    const isExpanded = expandedRows.has(i)
                    const weekend = isOffDay(d, timesheet?.work_days)
                    const holidayHrs = entry?.holiday_hours ?? 0
                    const vacHrs = Number(we?.vacation_hours) || 0
                    const hw = we
                      ? computeHoursWorked(
                          we.clock_in_1,
                          we.clock_out_1,
                          we.clock_in_2,
                          we.clock_out_2,
                        )
                      : 0
                    const dayTotal = hw + holidayHrs + vacHrs

                    renderedRows.push(
                      <tr
                        key={d}
                        className={`${styles.dayRow} ${weekend ? styles.weekendRow : ''} ${vacHrs > 0 ? styles.ptoRow : ''}`}
                      >
                        <td className={styles.dayLabel}>{formatDateShort(d)}</td>
                        <td className={styles.timeCell}>
                          <div className={styles.timeInCell}>
                            {isEditable &&
                            !weekend &&
                            hasWorkedTime(
                              we ?? { clock_in_1: '', clock_out_1: '', clock_in_2: '', clock_out_2: '' },
                            ) ? (
                              <Tooltip content="Copy this day's worked time to all other working days">
                                <button
                                  type="button"
                                  className={styles.copyButton}
                                  onClick={() => handleCopyClick(i)}
                                  aria-label="Copy this day's worked time to all other working days"
                                >
                                  <WandSparkles size={14} aria-hidden="true" />
                                </button>
                              </Tooltip>
                            ) : (
                              <span className={styles.copyButtonSpacer} aria-hidden="true" />
                            )}
                            <input
                              className={styles.timeInput}
                              type="time"
                              disabled={!isEditable}
                              {...form.register(`entries.${i}.clock_in_1`)}
                            />
                          </div>
                        </td>
                        <td className={styles.timeCell}>
                          <input
                            className={styles.timeInput}
                            type="time"
                            disabled={!isEditable}
                            {...form.register(`entries.${i}.clock_out_1`)}
                          />
                        </td>
                        <td className={styles.expandCell}>
                          <button
                            type="button"
                            className={styles.expandButton}
                            onClick={() => toggleExpand(i)}
                            disabled={!isEditable}
                            title={isExpanded ? 'Remove second time pair' : 'Add second time pair'}
                          >
                            {isExpanded ? (
                              <Minus size={14} aria-hidden="true" />
                            ) : (
                              <Plus size={14} aria-hidden="true" />
                            )}
                          </button>
                        </td>
                        <td className={isExpanded ? styles.timeCell : styles.emptyCell}>
                          {isExpanded ? (
                            <input
                              className={styles.timeInput}
                              type="time"
                              disabled={!isEditable}
                              {...form.register(`entries.${i}.clock_in_2`)}
                            />
                          ) : null}
                        </td>
                        <td className={isExpanded ? styles.timeCell : styles.emptyCell}>
                          {isExpanded ? (
                            <input
                              className={styles.timeInput}
                              type="time"
                              disabled={!isEditable}
                              {...form.register(`entries.${i}.clock_out_2`)}
                            />
                          ) : null}
                        </td>
                        <td className={styles.computedCell}>{hw > 0 ? hw.toFixed(2) : ''}</td>
                        <td className={`${styles.computedCell} ${styles.readonlyValue}`}>
                          {holidayHrs > 0 ? holidayHrs : ''}
                        </td>
                        <td className={styles.vacationCell}>
                          <input
                            className={styles.hoursInput}
                            type="number"
                            step="0.5"
                            min="0"
                            max="24"
                            disabled={!isEditable}
                            {...form.register(`entries.${i}.vacation_hours`, {
                              // A number input reports '' for anything that is not a
                              // parseable number — a cleared field, or a partial entry
                              // like '-' or '.'. valueAsNumber maps that to NaN, which
                              // JSON.stringify sends as null and the API rejects (422).
                              // Clearing the box means "no PTO", so normalise to 0.
                              setValueAs: toHours,
                            })}
                          />
                        </td>
                        <td className={styles.computedCell}>
                          <strong>{dayTotal > 0 ? dayTotal.toFixed(2) : ''}</strong>
                        </td>
                        <td className={styles.commentCell}>
                          {(() => {
                            const hasComment = !!(we?.comment)
                            if (isEditable) {
                              return (
                                <button
                                  type="button"
                                  className={hasComment ? styles.commentIconFilled : styles.commentIconEmpty}
                                  onClick={() => setCommentPopover({ index: i, readOnly: false })}
                                  title={hasComment ? 'Edit comment' : 'Add comment'}
                                >
                                  {hasComment ? (
                                    <MessageSquareText size={15} aria-hidden="true" />
                                  ) : (
                                    <MessageSquarePlus size={15} aria-hidden="true" />
                                  )}
                                </button>
                              )
                            }
                            if (hasComment) {
                              return (
                                <button
                                  type="button"
                                  className={styles.commentIconFilled}
                                  onClick={() => setCommentPopover({ index: i, readOnly: true })}
                                  title="View comment"
                                >
                                  <MessageSquareText size={15} aria-hidden="true" />
                                </button>
                              )
                            }
                            return null
                          })()}
                        </td>
                      </tr>,
                    )
                  }
                }

                // Final week subtotal
                if (lastWeekSunday !== null) {
                  const subtotal = weekSubtotals.find((ws) => ws.weekSunday === lastWeekSunday)
                  if (subtotal) {
                    renderedRows.push(
                      <tr key={`ws-${lastWeekSunday}`} className={styles.weekSubtotalRow}>
                        <td className={styles.weekSubtotalLabel}>
                            Week Total
                            {subtotal.isIncomplete && (
                              <span style={{ color: colors.gray50, fontWeight: 'normal', marginLeft: 4, fontSize: '0.75rem' }}>
                                (partial week)
                              </span>
                            )}
                          </td>
                          <td className={styles.weekSubtotalValue} colSpan={5}></td>
                          <td className={styles.weekSubtotalValue}></td>
                          <td className={styles.weekSubtotalValue}></td>
                          <td className={styles.weekSubtotalValue}></td>
                          <td className={styles.weekSubtotalValue}></td>
                          <td className={styles.weekSubtotalValue}>
                            {subtotal.totalHours > 0 ? subtotal.totalHours.toFixed(2) : ''}
                            {subtotal.isIncomplete ? (
                              <span className={styles.otIndicator} title="OT pending — week spans into next pay period">
                                <span className={styles.otDotPending} />
                              </span>
                            ) : (
                              <>
                                {subtotal.flsaOvertimeHours > 0 && (
                                  <span className={styles.otIndicator} title={`${subtotal.flsaOvertimeHours.toFixed(2)} hrs OT (1.5x)`}>
                                    <span className={styles.otDotFlsa} />
                                    <span className={styles.otValueFlsa}>{subtotal.flsaOvertimeHours.toFixed(2)}</span>
                                  </span>
                                )}
                                {subtotal.straightOvertimeHours > 0 && (
                                  <span className={styles.otIndicator} title={`${subtotal.straightOvertimeHours.toFixed(2)} hrs Straight OT (1.0x)`}>
                                    <span className={styles.otDotStraight} />
                                    <span className={styles.otValueStraight}>{subtotal.straightOvertimeHours.toFixed(2)}</span>
                                  </span>
                                )}
                              </>
                            )}
                          </td>
                      </tr>,
                    )
                  }
                }

                return renderedRows
              })()}

              {/* Totals row */}
              <tr className={styles.totalsRow}>
                <td className={styles.totalLabel}>Period Total</td>
                <td className={styles.totalValue}></td>
                <td className={styles.totalValue}></td>
                <td className={styles.totalValue}></td>
                <td className={styles.totalValue}></td>
                <td className={styles.totalValue}></td>
                <td className={styles.totalValue}>{totals.hoursWorked.toFixed(2)}</td>
                <td className={styles.totalValue}>
                  {totals.holidayHours > 0 ? totals.holidayHours.toFixed(0) : '0'}
                </td>
                <td className={styles.totalValue}>{totals.vacationHours.toFixed(0)}</td>
                <td className={styles.totalValue}>
                  <strong>{totals.total.toFixed(2)}</strong>
                </td>
                <td className={styles.totalValue}></td>
              </tr>
            </tbody>
          </table>
        </div>
      </form>


      {/* Comment popover */}
      {commentPopover !== null && (
        <div className={styles.confirmOverlay} onClick={() => setCommentPopover(null)}>
          <div className={styles.confirmDialog} onClick={(e) => e.stopPropagation()}>
            <div className={styles.confirmTitle}>
              {commentPopover.readOnly ? 'Comment' : (form.getValues(`entries.${commentPopover.index}.comment`) ? 'Edit Comment' : 'Add Comment')}
            </div>
            <div style={{ marginBottom: 8, fontSize: '0.85rem', color: colors.gray70 }}>
              {formatDate(dates[commentPopover.index])}
            </div>
            {commentPopover.readOnly ? (
              <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem', marginBottom: 16 }}>
                {form.getValues(`entries.${commentPopover.index}.comment`) || 'No comment.'}
              </div>
            ) : (
              <textarea
                className={styles.reasonInput}
                placeholder="Add a note about this day..."
                maxLength={500}
                defaultValue={form.getValues(`entries.${commentPopover.index}.comment`)}
                onChange={(e) => form.setValue(`entries.${commentPopover.index}.comment`, e.target.value)}
                autoFocus
              />
            )}
            <div className={styles.confirmActions}>
              <Button variant="outline" size="sm" onClick={() => setCommentPopover(null)}>
                {commentPopover.readOnly ? 'Close' : 'Done'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Copy-to-working-days confirmation dialog (shown only when overwriting) */}
      {copyConfirm !== null && (
        <div className={styles.confirmOverlay} onClick={() => setCopyConfirm(null)}>
          <div className={styles.confirmDialog} onClick={(e) => e.stopPropagation()}>
            <div className={styles.confirmTitle}>Copy worked time to all working days?</div>
            <div className={styles.confirmText}>
              This will copy {formatDate(dates[copyConfirm.sourceIndex])}'s clock in/out times to all
              other working days in this pay period.{' '}
              {copyConfirm.overwriteCount === 1
                ? '1 working day already has different worked time that will be overwritten.'
                : `${copyConfirm.overwriteCount} working days already have different worked time that will be overwritten.`}{' '}
              Holiday and PTO hours are not affected.
            </div>
            <div className={styles.confirmActions}>
              <Button variant="outline" size="sm" onClick={() => setCopyConfirm(null)}>
                Cancel
              </Button>
              <Button
                size="sm"
                autoFocus
                onClick={() => {
                  applyCopy(copyConfirm.sourceIndex)
                  setCopyConfirm(null)
                }}
              >
                Copy &amp; Overwrite
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Submit confirmation dialog */}
      {showConfirm && (
        <div className={styles.confirmOverlay} onClick={() => setShowConfirm(false)}>
          <div className={styles.confirmDialog} onClick={(e) => e.stopPropagation()}>
            <div className={styles.confirmTitle}>Submit Timesheet?</div>
            <div className={styles.confirmText}>
              You are about to submit {totals.total.toFixed(1)} hours for approval. Once submitted,
              you will not be able to edit this timesheet until it is returned by your manager.
            </div>
            <div className={styles.confirmActions}>
              <Button variant="outline" size="sm" onClick={() => setShowConfirm(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleConfirmSubmit}
                disabled={submitTimesheet.isPending}
              >
                {submitTimesheet.isPending ? 'Submitting...' : 'Confirm Submit'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
